import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatusPill from "../../components/StatusPill/StatusPill";
import TelegramThumb from "../Bots/Flows/components/FlowNode/TelegramThumb";
import { useNotification } from "../../components/Notifications/NotificationContext";

const MEDIA_ICONS = {
    photo: "fas fa-image",
    video: "fas fa-video",
    voice: "fas fa-microphone",
};

const fmtBRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function BotMediaThumb({ media, botId }) {
    // Vídeos têm thumbnail_file_id; voice não tem preview visual
    const fileId = media.thumbnail_file_id || (media.media_type === "photo" ? media.file_id : null);

    if (!fileId) {
        return (
            <div className="admin-media-placeholder">
                <i className={MEDIA_ICONS[media.media_type] || "fas fa-file"} />
                {media.media_type}
            </div>
        );
    }

    return <TelegramThumb fileId={fileId} botId={botId} imgClassName="" />;
}

function BotCopyPreview({ text }) {
    const [expanded, setExpanded] = useState(false);

    if (!text?.trim()) {
        return <div className="admin-copy-preview admin-copy-empty">Sem copy configurada</div>;
    }

    const isLong = text.length > 220 || text.split("\n").length > 4;

    return (
        <>
            <div className={`admin-copy-preview ${expanded ? "" : "is-clamped"}`}>{text}</div>
            {isLong && (
                <button type="button" className="admin-copy-toggle" onClick={() => setExpanded(!expanded)}>
                    {expanded ? "ver menos" : "ver mais"}
                </button>
            )}
        </>
    );
}

export default function AdminBots() {
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(false);
    const { showError, showSuccess } = useNotification();
    const navigate = useNavigate();
    // order: "revenue" (maior faturamento primeiro, padrão) | "recent" (atividade)
    const [filters, setFilters] = useState({ username: "", blocked: false, order: "revenue" });
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadBots(1);
    }, []);

    const loadBots = async (page = 1, override = null) => {
        const f = override || filters;
        try {
            setLoading(true);
            const { data } = await dashboardService.getAdminBots({
                page,
                username: f.username,
                blocked: f.blocked ? "true" : "",
                order: f.order,
            });
            setBots(data.bots || []);
            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar bots.");
        } finally {
            setLoading(false);
        }
    };

    const handleBlock = async (bot) => {
        const ok = window.confirm(
            `Bloquear @${bot.bot_username || bot.id}?\n\nO bot para de responder imediatamente e o dono não consegue reativá-lo.`
        );
        if (!ok) return;

        try {
            await dashboardService.blockBot(bot.id);
            showSuccess(`Bot @${bot.bot_username || bot.id} bloqueado.`);
            loadBots(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao bloquear. " + (err?.response?.data?.error || err?.message || ""));
        }
    };

    const handleUnblock = async (bot) => {
        const ok = window.confirm(
            `Desbloquear @${bot.bot_username || bot.id}?\n\nO bot volta a funcionar imediatamente.`
        );
        if (!ok) return;

        try {
            const { data } = await dashboardService.unblockBot(bot.id);
            if (data.started) {
                showSuccess(`Bot @${bot.bot_username || bot.id} desbloqueado e iniciado.`);
            } else {
                showSuccess(data.message || `Bot desbloqueado, mas não foi possível iniciá-lo.`);
            }
            loadBots(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao desbloquear. " + (err?.response?.data?.error || err?.message || ""));
        }
    };

    const statusPill = (bot) => {
        if (bot.is_blocked) return <StatusPill color="red">Bloqueado</StatusPill>;
        if (bot.is_running) return <StatusPill color="green" pulse>Rodando</StatusPill>;
        return <StatusPill color="gray">Parado</StatusPill>;
    };

    return (
        <>
            {/* FILTROS */}
            <div className="filters-section">
                <div className="filters-grid">
                    <div className="filter-group">
                        <input
                            type="text"
                            placeholder="@ do bot"
                            value={filters.username}
                            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
                            className="filter-input"
                        />
                    </div>

                    <label className="admin-check">
                        <input
                            type="checkbox"
                            checked={filters.blocked}
                            onChange={(e) => {
                                const next = { ...filters, blocked: e.target.checked };
                                setFilters(next);
                                loadBots(1, next);
                            }}
                        />
                        Somente bloqueados
                    </label>

                    <div className="sx-filterbar">
                        {[["revenue", "Maior faturamento"], ["recent", "Mais recentes"]].map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                className={`sx-filter-chip ${filters.order === id ? "is-active" : ""}`}
                                onClick={() => {
                                    const next = { ...filters, order: id };
                                    setFilters(next);
                                    loadBots(1, next);
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <button onClick={() => loadBots(1)} className="btn-primary">
                        Aplicar Filtros
                    </button>
                </div>
            </div>

            {/* CARDS DE BOTS */}
            <div className="admin-cards">
                {bots.map((bot) => (
                    <div key={bot.id} className="admin-card">
                        <div className="admin-card-head">
                            <div className="admin-avatar">
                                {bot.bot_photo_file_id ? (
                                    <TelegramThumb fileId={bot.bot_photo_file_id} botId={bot.id} imgClassName="" />
                                ) : (
                                    <i className="fas fa-robot" />
                                )}
                            </div>

                            <div className="admin-card-id">
                                <div className="admin-card-name-row">
                                    <span className="admin-card-name">
                                        {bot.bot_username ? `@${bot.bot_username}` : (bot.bot_name || `Bot #${bot.id}`)}
                                    </span>
                                </div>
                                <span className="admin-card-sub">
                                    <i className="fas fa-user" /> {bot.owner_username} · {bot.owner_email}
                                </span>
                                <div className="admin-bot-dates">
                                    <span><i className="fas fa-calendar-plus" />Criado: {fmtDateTimezone(bot.created_at)}</span>
                                    <span>
                                        <i className="fas fa-pen" />
                                        Editado: {bot.updated_at ? fmtDateTimezone(bot.updated_at) : "—"}
                                    </span>
                                    <span>
                                        <i className="fas fa-clock" />
                                        Atividade: {bot.last_activity ? fmtDateTimezone(bot.last_activity) : "—"}
                                    </span>
                                </div>
                            </div>

                            {statusPill(bot)}
                        </div>

                        <div className="admin-card-stats">
                            <div className="admin-stat">
                                <span className="admin-stat-label">Faturamento</span>
                                <span className="admin-stat-value is-green">{fmtBRL(bot.revenue)}</span>
                            </div>
                            <div className="admin-stat">
                                <span className="admin-stat-label">Vendas</span>
                                <span className="admin-stat-value is-blue">{(bot.sales || 0).toLocaleString("pt-BR")}</span>
                                <span className="admin-stat-sub">aprovadas</span>
                            </div>
                        </div>

                        <BotCopyPreview text={bot.welcome_message} />

                        {(bot.plans || []).length > 0 && (
                            <div className="admin-gateways">
                                {bot.plans.map((p, i) => (
                                    <span key={`${bot.id}-plan-${i}`} className="admin-plan-badge">
                                        <i className="fas fa-tag" />
                                        {p.name || `Plano ${i + 1}`}
                                        {p.value != null && <b>{fmtBRL(p.value)}</b>}
                                        {p.duration && <em>{p.duration}</em>}
                                    </span>
                                ))}
                            </div>
                        )}

                        {(bot.media || []).length > 0 && (
                            <div className="admin-bot-media">
                                {bot.media.map((m, i) => (
                                    <BotMediaThumb key={`${bot.id}-${i}`} media={m} botId={bot.id} />
                                ))}
                            </div>
                        )}

                        <div className="admin-card-footer">
                            <button
                                type="button"
                                className="admin-icon-btn"
                                title="Abrir a configuração deste bot"
                                onClick={() => navigate(`/bots/manage/${bot.id}?tab=edit`)}
                            >
                                <i className="fas fa-pen" />
                                <span>Editar bot</span>
                            </button>
                            <button
                                type="button"
                                className="admin-icon-btn"
                                title="Ver e editar os disparos deste bot"
                                onClick={() => navigate(`/disparos?bot=${bot.id}`)}
                            >
                                <i className="fas fa-paper-plane" />
                                <span>Disparos</span>
                            </button>
                            {bot.is_blocked ? (
                                <button
                                    type="button"
                                    className="admin-icon-btn is-success"
                                    title="Desbloquear bot"
                                    onClick={() => handleUnblock(bot)}
                                >
                                    <i className="fas fa-unlock" />
                                    <span>Desbloquear</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="admin-icon-btn is-danger"
                                    title="Bloquear bot (para de responder na hora)"
                                    onClick={() => handleBlock(bot)}
                                >
                                    <i className="fas fa-ban" />
                                    <span>Bloquear bot</span>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {bots.length === 0 && !loading && (
                <div className="empty-state">
                    <i className="fas fa-robot" />
                    <h4>Nenhum bot encontrado</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && bots.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} ({pagination.total} bots)
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => loadBots(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary"
                        >
                            <i className="fas fa-chevron-left"></i> Anterior
                        </button>

                        <button
                            onClick={() => loadBots(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary"
                        >
                            Próximo <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
